import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import type { AlertDraft, AlertMetricCondition } from '../models/alert.types'
import type { AlertReportFilter } from '../models/reports-bridge'
import type { AlertStepId } from '../service/alertValidator'

import { useI18n } from '@/shared/i18n'

type SummaryKey =
  'name' | 'level' | 'groupBy' | 'filters' | 'window' | 'metrics' | 'conditions' | 'channels'

interface SummaryBase {
  key: SummaryKey
  label: string
  step: AlertStepId
}

export type AlertSummarySection =
  | (SummaryBase & { kind: 'text'; value: string | null })
  | (SummaryBase & { kind: 'chips'; values: string[] })
  | (SummaryBase & { kind: 'filters'; values: AlertReportFilter[] })
  | (SummaryBase & {
      kind: 'conditions'
      values: AlertMetricCondition[]
      operator?: 'and' | 'or'
      join?: 'AND' | 'OR'
    })

export interface UseAlertSummary {
  sections: ComputedRef<AlertSummarySection[]>
}

export function useAlertSummary(draft: MaybeRefOrGetter<AlertDraft>): UseAlertSummary {
  const { t } = useI18n()

  const sections = computed<AlertSummarySection[]>(() => {
    const current = toValue(draft)
    const window = current.window
      ? current.window === 'custom'
        ? t('alerts.wizard.customWindow', { days: current.customWindowDays ?? 0 })
        : t(`alerts.window.${current.window}`)
      : null

    return [
      {
        key: 'name',
        kind: 'text',
        label: t('alerts.summary.name'),
        step: 'monitor',
        value: current.name.trim() || null,
      },
      {
        key: 'level',
        kind: 'text',
        label: t('alerts.summary.level'),
        step: 'monitor',
        value: current.level?.name ?? null,
      },
      {
        key: 'groupBy',
        kind: 'chips',
        label: t('alerts.summary.groupBy'),
        step: 'monitor',
        values: current.groupBy.map((column) => column.header),
      },
      {
        key: 'filters',
        kind: 'filters',
        label: t('alerts.summary.filters'),
        step: 'filter',
        values: current.filters,
      },
      {
        key: 'window',
        kind: 'text',
        label: t('alerts.summary.window'),
        step: 'conditions',
        value: window,
      },
      {
        key: 'metrics',
        kind: 'chips',
        label: t('alerts.summary.metrics'),
        step: 'conditions',
        values: current.metrics.map((condition) => condition.column.header),
      },
      {
        key: 'conditions',
        kind: 'conditions',
        label: t('alerts.summary.conditions'),
        step: 'conditions',
        values: current.metrics,
        operator: current.conditionJoin === 'AND' ? 'and' : 'or',
        join: current.conditionJoin,
      },
      {
        key: 'channels',
        kind: 'chips',
        label: t('alerts.summary.channels'),
        step: 'notify',
        values: current.channels.map((channel) => t(`alerts.notify.channel.${channel}`)),
      },
    ]
  })

  return { sections }
}
